import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { Map } from 'immutable';

import { Col, Row } from 'react-bootstrap';
import cn from 'classnames';
import CheckoutController from '../../../event_components/Checkout';
import Title from '../../../event_components/title';
import Details from '../../../event_components/details';
import AttendButton from '../../../event_components/attend_button';
import styles from './columns.scss';
import EventStatus from '../../../event_components/event_status';
import TicketPurchasing from '../../../event_components/ticket-purchasing';
import AddEventToCalendar from '../../../event_components/add_to_calendar';
import { LocaleWrapper } from '../../../../locale-wrapper';
import TwitchPreviousVideo from '../../../event_components/twitch-video';
import { attendButtonIsVisible } from '../../shared/utils';
import PaginationContainer from '../../shared/pagination_container';
import { EventTime } from '../../../event_components/event-time';
import { EventDate } from '../../../event_components/event-date';
import { LocationItem } from '../../shared/location-item/location-item';
import Gallery from '../../shared/gallery';
import ModalWithProps from '../../../modal';
import { Divider } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import WatchLaterSharpIcon from '@mui/icons-material/WatchLaterSharp';
import { EventOccurrences } from '../../../event_components/event-occurrences';

const propTypes = {
  event: PropTypes.instanceOf(Map).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
};

export const ListEvent = ({ event, settings, onResizeComponent, onEventRegister, forceButstonNewRow, isCheckoutOpened, onCheckoutOpen, columns }) => {
  if (event.get('initial_embed') || event.get('is_offline')) {
    return event.get('is_offline') ? (
      <TwitchPreviousVideo settings={settings} event={event} />
    ) : (
      <TicketPurchasing event={event} settings={settings} open onResizeComponent={onResizeComponent} />
    );
  }
  const toggleAttendButton = useCallback(() => {
    if (event.get('type') === 'google') {
      return;
    }
    onEventRegister && onEventRegister(event);
    onResizeComponent();
    onCheckoutOpen({ id: event.get('id'), isOpen: true });
  }, [event, onEventRegister]);

  const handleOnCloseCheckout = useCallback(() => {
    onCheckoutOpen({ id: event.get('id'), isOpen: false });
  }, [event]);

  return (
    <div className={cn(styles.container, { [styles.center]: forceButstonNewRow, [styles.open]: isCheckoutOpened })}>
      <EventDetails
        event={event}
        settings={settings}
        columns={columns}
        isCheckoutOpened={isCheckoutOpened}
        onResizeComponent={onResizeComponent}
        toggleAttendButton={toggleAttendButton}
      />
      <div className={styles.checkoutWrapper}>
        <CheckoutController
          variant="redesign"
          event={event}
          settings={settings}
          inClassName={cn('checkout-fade', styles.checkoutFade)}
          openTicketing={isCheckoutOpened}
          onResizeComponent={onResizeComponent}
          onClose={handleOnCloseCheckout}
        />
      </div>
      <ModalWithProps />
    </div>
  );
};

ListEvent.propTypes = propTypes;

export const EventDetails = ({ event, settings, columns, isCheckoutOpened, onResizeComponent, toggleAttendButton }) => (
  <Row className={cn(styles.list, 'event', { [styles.open]: isCheckoutOpened })}>
    {columns === 1 ? (
      <Col className={cn({ [styles.galleryCenter]: columns === 1 })}>
        <Gallery event={event} />
      </Col>
    ) : (
      <Gallery event={event} />
    )}
    <div className={styles.cartInfoWrapper}>
      {columns === 1 ? (
        <Col className="no-padding">
          <Row className={styles.verticalDetails}>
            <div className={cn(styles.verticalItem, styles.title)}>
              <Title event={event} settings={settings} />
            </div>
            <Divider orientation="vertical" className="vertical-divider" />
            {event.get('upcoming_occurrences') ? (
              // Render when there are upcoming events
              <div className={styles.verticalItem}>
                <DateRangeIcon alt="date" width="20px" className="date" />
                <EventOccurrences event={event} settings={settings} />
              </div>
            ) : (
              // Render when there are no upcoming events
              <>
                <div className={styles.verticalItem}>
                  <DateRangeIcon alt="date" width="20px" className="date" />
                  <EventDate event={event} settings={settings} />
                </div>
                <Divider orientation="vertical" className="vertical-divider" />
                <div className={styles.verticalItem}>
                  <WatchLaterSharpIcon alt="time" width="20px" className="date" />
                  <EventTime event={event} settings={settings} />
                </div>
              </>
            )}
            {settings.get('showEventStatus') && (
              <>
                <Divider orientation="vertical" className="vertical-divider" />
                <div className={styles.verticalItem}>
                  <EventStatus event={event} settings={settings} />
                </div>
              </>
            )}
            {settings.get('showEventStatus') && (
              <>
                <Divider orientation="vertical" className="vertical-divider" />
                <div className={styles.verticalItem}>
                  <AddEventToCalendar event={event} settings={settings} className={styles.addToCalendar} />
                </div>
              </>
            )}
          </Row>
          <Details event={event} settings={settings} className={styles.description} onResizeComponent={onResizeComponent} />
        </Col>
      ) : (
        <Col className={styles.details}>
          <Title event={event} settings={settings} />
          <div className={styles.eventFactsList}>
            <LocationItem event={event} settings={settings} className={styles.locationItem} />
            {event.get('upcoming_occurrences') ? (
              // Render when there are upcoming events
              <div className={styles.factItem}>
                <DateRangeIcon alt="date" width="20px" className="date" />
                <EventOccurrences event={event} settings={settings} />
              </div>
            ) : (
              // Render when there are no upcoming events
              <>
                <div className={styles.factItem}>
                  <DateRangeIcon alt="date" width="20px" className="date" />
                  <EventDate event={event} settings={settings} />
                </div>
                <div className={styles.factItem}>
                  <WatchLaterSharpIcon alt="time" width="20px" className="date" />
                  <EventTime event={event} settings={settings} />
                </div>
              </>
            )}
          </div>
          {settings.get('showEventStatus') && <EventStatus event={event} settings={settings} />}
          {settings.get('enableAddToCalendar') && <AddEventToCalendar event={event} settings={settings} className={styles.addToCalendar} />}
          <Details event={event} settings={settings} className={styles.description} onResizeComponent={onResizeComponent} />
        </Col>
      )}
    </div>
    {attendButtonIsVisible(event, settings, isCheckoutOpened) && (
      <AttendButton isFullWidth event={event} settings={settings} handleButtonClick={toggleAttendButton} />
    )}
  </Row>
);

export default function Columns({ events, settings, onResizeComponent, forceButtonNewRow, onEventRegister, columns }) {
  const [isCheckoutOpened, setIsCheckoutOpened] = useState(false);
  const [eventWithCheckoutOpenId, setEventWithCheckoutOpenId] = useState();

  const handleOpenCheckout = useCallback(({ id, isOpen }) => {
    setIsCheckoutOpened(isOpen);
    setEventWithCheckoutOpenId(id);
  }, []);

  if (isCheckoutOpened) {
    const openedEvent = events.find((event) => event.get('id') === eventWithCheckoutOpenId);

    return (
      <LocaleWrapper event={openedEvent}>
        <ListEvent
          key={openedEvent.get('id')}
          event={openedEvent}
          settings={settings}
          onResizeComponent={onResizeComponent}
          onEventRegister={onEventRegister}
          forceButtonNewRow={forceButtonNewRow}
          isCheckoutOpened={isCheckoutOpened}
          onCheckoutOpen={handleOpenCheckout}
        />
      </LocaleWrapper>
    );
  }

  const renderEvent = (event) => (
    <LocaleWrapper event={event}>
      <ListEvent
        key={event.get('id')}
        event={event}
        settings={settings}
        onResizeComponent={onResizeComponent}
        onEventRegister={onEventRegister}
        forceButtonNewRow={forceButtonNewRow}
        onCheckoutOpen={handleOpenCheckout}
        columns={columns}
      />
    </LocaleWrapper>
  );

  return (
    <PaginationContainer events={events} settings={settings} onResizeComponent={onResizeComponent} renderEvent={renderEvent} columns={columns} />
  );
}

Columns.propTypes = {
  events: PropTypes.instanceOf(Map).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
};
